import { useMemo } from 'react';
import { BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import MenuIcon from '@mui/icons-material/Menu';
import { useUser } from '../contexts/UserContext';

const MobileBottomNav = ({ onMoreClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useUser();

  const allowedPages = userProfile?.ui_config?.allowed_pages || [];
  const alwaysAllowed = [
    'DashboardPage', 'ClientsListPageCached', 'SuppliersPage', 'CashFlowPage',
  ];

  const hasAccess = (pageName) => {
    return alwaysAllowed.includes(pageName) || allowedPages.includes(pageName);
  };

  const navItems = useMemo(() => {
    // Order: right-to-left on mobile (RTL) — תפריט, לקוחות, ראשי, תנועות, ספקים.
    // MUI BottomNavigation reverses JSX order under RTL, so the first push renders
    // rightmost on screen.
    const items = [];
    items.push({ label: 'תפריט', icon: <MenuIcon />, path: '__more__' });
    if (hasAccess('ClientsListPageCached')) {
      items.push({ label: 'לקוחות', icon: <BusinessIcon />, path: '/clients' });
    }
    if (hasAccess('DashboardPage')) {
      items.push({ label: 'ראשי', icon: <DashboardIcon />, path: '/dashboard' });
    }
    if (hasAccess('TransactionsPage') || hasAccess('CashFlowPage')) {
      items.push({ label: 'תנועות', icon: <PaymentIcon />, path: '/transactions' });
    }
    if (hasAccess('SuppliersPage')) {
      items.push({ label: 'ספקים', icon: <LocalShippingIcon />, path: '/suppliers' });
    }
    return items;
  }, [allowedPages]);

  const currentIndex = navItems.findIndex((item) =>
    item.path !== '__more__' && location.pathname.startsWith(item.path)
  );

  const handleChange = (event, newValue) => {
    const item = navItems[newValue];
    if (item.path === '__more__') {
      onMoreClick();
    } else {
      navigate(item.path);
    }
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        borderTop: '1px solid',
        borderColor: 'divider',
        // Safe area for devices with home indicator (iPhone X+)
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
      elevation={8}
    >
      <BottomNavigation
        value={currentIndex >= 0 ? currentIndex : false}
        onChange={handleChange}
        showLabels
        sx={{
          height: 56,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            py: 0.5,
            px: 0.5,
            gap: '2px',
            '&.Mui-selected': {
              color: 'primary.main',
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.65rem',
            fontWeight: 500,
            '&.Mui-selected': {
              fontSize: '0.7rem',
              fontWeight: 700,
            },
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.3rem',
          },
        }}
      >
        {navItems.map((item) => (
          <BottomNavigationAction
            key={item.label}
            label={item.label}
            icon={item.icon}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

export default MobileBottomNav;
